// src/utils/providerOutputColumnUtils.js
// -----------------------------------------------------------------------------
// Generic provider output column helpers for Display Table / Submissions Table.
//
// Enterprise boundary:
//  • This file is provider-agnostic.
//  • It knows about provider-owned virtual columns.
//  • It does NOT know Bitly, HubSpot, Slack, BigQuery, etc.
//  • Provider-specific labels, value paths, pending text, and metric definitions
//    belong in provider adapter files, e.g.
//    src/integrations/providers/bitly/bitlyProviderColumns.js
//
// Column conventions:
//  • providerOutput@<provider>@<outputKey>
//  • providerMetric@<provider>@<metricKey>
// -----------------------------------------------------------------------------

export const PROVIDER_COLUMN_ROLES = Object.freeze({
  OUTPUT: 'providerOutput',
  METRIC: 'providerMetric',
});

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizeProviderId(provider) {
  return normalizeString(provider).toLowerCase();
}

export function normalizeOutputKey(outputKey) {
  return normalizeString(outputKey);
}

export function normalizeMetricKey(metricKey) {
  return normalizeString(metricKey);
}

function normalizePathArray(paths) {
  if (!Array.isArray(paths)) return [];

  return paths
    .map((path) => normalizeString(path))
    .filter(Boolean);
}

export function getProviderOutputColumnId({
  provider,
  outputKey,
}) {
  const providerId = normalizeProviderId(provider);
  const key = normalizeOutputKey(outputKey);

  if (!providerId || !key) return '';

  return `${PROVIDER_COLUMN_ROLES.OUTPUT}@${providerId}@${key}`;
}

export function getProviderMetricColumnId({
  provider,
  metricKey,
}) {
  const providerId = normalizeProviderId(provider);
  const key = normalizeMetricKey(metricKey);

  if (!providerId || !key) return '';

  return `${PROVIDER_COLUMN_ROLES.METRIC}@${providerId}@${key}`;
}

export function parseProviderColumnId(columnOrId) {
  const id =
    typeof columnOrId === 'string'
      ? columnOrId
      : normalizeString(columnOrId?.id);

  if (!id) {
    return {
      isProviderColumn: false,
      role: '',
      provider: '',
      outputKey: '',
      metricKey: '',
    };
  }

  const parts = id.split('@');
  const role = parts[0] || '';
  const provider = normalizeProviderId(parts[1] || '');
  const key = normalizeString(parts.slice(2).join('@'));

  if (
    role !== PROVIDER_COLUMN_ROLES.OUTPUT &&
    role !== PROVIDER_COLUMN_ROLES.METRIC
  ) {
    return {
      isProviderColumn: false,
      role: '',
      provider: '',
      outputKey: '',
      metricKey: '',
    };
  }

  if (!provider || !key) {
    return {
      isProviderColumn: false,
      role: '',
      provider: '',
      outputKey: '',
      metricKey: '',
    };
  }

  return {
    isProviderColumn: true,
    role,
    provider,
    outputKey: role === PROVIDER_COLUMN_ROLES.OUTPUT ? key : '',
    metricKey: role === PROVIDER_COLUMN_ROLES.METRIC ? key : '',
  };
}

export function getProviderColumnSpec(column = {}) {
  const parsed = parseProviderColumnId(column);

  const explicitRole = normalizeString(column?.role);
  const explicitProvider = normalizeProviderId(column?.provider);
  const explicitOutputKey = normalizeOutputKey(column?.outputKey);
  const explicitMetricKey = normalizeMetricKey(column?.metricKey);

  const role = explicitRole || parsed.role;
  const provider = explicitProvider || parsed.provider;

  const outputKey =
    role === PROVIDER_COLUMN_ROLES.OUTPUT
      ? explicitOutputKey || parsed.outputKey
      : '';

  const metricKey =
    role === PROVIDER_COLUMN_ROLES.METRIC
      ? explicitMetricKey || parsed.metricKey
      : '';

  return {
    isProviderColumn:
      (
        role === PROVIDER_COLUMN_ROLES.OUTPUT &&
        Boolean(provider) &&
        Boolean(outputKey)
      ) ||
      (
        role === PROVIDER_COLUMN_ROLES.METRIC &&
        Boolean(provider) &&
        Boolean(metricKey)
      ),
    role,
    provider,
    outputKey,
    metricKey,
  };
}

export function isProviderOutputColumn(column = {}) {
  const spec = getProviderColumnSpec(column);

  return (
    spec.role === PROVIDER_COLUMN_ROLES.OUTPUT &&
    Boolean(spec.provider) &&
    Boolean(spec.outputKey)
  );
}

export function isProviderMetricColumn(column = {}) {
  const spec = getProviderColumnSpec(column);

  return (
    spec.role === PROVIDER_COLUMN_ROLES.METRIC &&
    Boolean(spec.provider) &&
    Boolean(spec.metricKey)
  );
}

export function isProviderColumn(column = {}) {
  return isProviderOutputColumn(column) || isProviderMetricColumn(column);
}

export function getProviderGroupId(provider) {
  return normalizeProviderId(provider);
}

export function getFallbackProviderGroupLabel(provider) {
  const providerId = normalizeProviderId(provider);

  if (!providerId) return 'Provider';

  return providerId.charAt(0).toUpperCase() + providerId.slice(1);
}

export function normalizeColumnOrder(columnOrder = []) {
  return (Array.isArray(columnOrder) ? columnOrder : [])
    .filter(Boolean)
    .map((column, index) => {
      if (typeof column === 'string') {
        return {
          id: column,
          visible: true,
          order: index,
        };
      }

      return {
        ...column,
        id: String(column.id || ''),
        visible: column.visible !== false,
        order: Number.isFinite(column.order) ? column.order : index,
      };
    })
    .filter((column) => Boolean(column.id))
    .map((column, index) => ({
      ...column,
      order: index,
    }));
}

export function getNextColumnOrder(columnOrder = []) {
  const normalized = normalizeColumnOrder(columnOrder);

  if (!normalized.length) return 0;

  return Math.max(
    ...normalized.map((column) =>
      Number.isFinite(column.order) ? column.order : 0
    )
  ) + 1;
}

export function buildProviderOutputColumn({
  provider,
  outputKey,
  label,
  sourceUfi = null,
  visible = true,
  order = null,
  groupId = null,
  groupLabel = null,
  valuePaths = [],
  statusPaths = [],
  errorPaths = [],
  pendingPaths = [],
  pendingText = 'Pending...',
  errorText = 'Unavailable',
  extra = {},
}) {
  const providerId = normalizeProviderId(provider);
  const key = normalizeOutputKey(outputKey);
  const id = getProviderOutputColumnId({
    provider: providerId,
    outputKey: key,
  });

  if (!providerId || !key || !id) return null;

  return {
    id,
    label: label || `${getFallbackProviderGroupLabel(providerId)} ${key}`,
    visible: visible !== false,
    order: Number.isFinite(order) ? order : 0,
    kind: 'virtual',
    role: PROVIDER_COLUMN_ROLES.OUTPUT,
    provider: providerId,
    outputKey: key,
    sourceUfi: sourceUfi || null,
    groupId: groupId || getProviderGroupId(providerId),
    ...(groupLabel ? { groupLabel } : {}),
    valuePaths: normalizePathArray(valuePaths),
    statusPaths: normalizePathArray(statusPaths),
    errorPaths: normalizePathArray(errorPaths),
    pendingPaths: normalizePathArray(pendingPaths),
    pendingText,
    errorText,
    ...extra,
  };
}

export function buildProviderMetricColumn({
  provider,
  metricKey,
  label,
  visible = false,
  order = null,
  groupId = null,
  groupLabel = null,
  valuePaths = [],
  statusPaths = [],
  errorPaths = [],
  pendingPaths = [],
  pendingText = 'Refreshing...',
  errorText = 'Unavailable',
  extra = {},
}) {
  const providerId = normalizeProviderId(provider);
  const key = normalizeMetricKey(metricKey);
  const id = getProviderMetricColumnId({
    provider: providerId,
    metricKey: key,
  });

  if (!providerId || !key || !id) return null;

  return {
    id,
    label: label || `${getFallbackProviderGroupLabel(providerId)} ${key}`,
    visible: visible === true,
    order: Number.isFinite(order) ? order : 0,
    kind: 'virtual',
    role: PROVIDER_COLUMN_ROLES.METRIC,
    provider: providerId,
    metricKey: key,
    groupId: groupId || getProviderGroupId(providerId),
    ...(groupLabel ? { groupLabel } : {}),
    valuePaths: normalizePathArray(valuePaths),
    statusPaths: normalizePathArray(statusPaths),
    errorPaths: normalizePathArray(errorPaths),
    pendingPaths: normalizePathArray(pendingPaths),
    pendingText,
    errorText,
    ...extra,
  };
}

export function upsertProviderColumn(
  columnOrder = [],
  column = null,
  options = {}
) {
  const {
    preserveExistingOrder = true,
    preserveExistingVisibility = false,
  } = options;

  if (!column?.id) {
    return normalizeColumnOrder(columnOrder);
  }

  const normalized = normalizeColumnOrder(columnOrder);
  const existingIndex = normalized.findIndex((item) => item.id === column.id);

  if (existingIndex === -1) {
    const nextOrder = Number.isFinite(column.order)
      ? column.order
      : normalized.length;

    return normalizeColumnOrder([
      ...normalized,
      {
        ...column,
        order: nextOrder,
      },
    ]);
  }

  return normalizeColumnOrder(
    normalized.map((item, index) => {
      if (index !== existingIndex) return item;

      return {
        ...item,
        ...column,
        order:
          preserveExistingOrder && Number.isFinite(item.order)
            ? item.order
            : Number.isFinite(column.order)
              ? column.order
              : item.order,
        visible:
          preserveExistingVisibility && typeof item.visible === 'boolean'
            ? item.visible
            : typeof column.visible === 'boolean'
              ? column.visible
              : item.visible !== false,
      };
    })
  );
}

export function upsertProviderColumns(
  columnOrder = [],
  columns = [],
  options = {}
) {
  return (Array.isArray(columns) ? columns : [])
    .filter(Boolean)
    .reduce(
      (acc, column) => upsertProviderColumn(acc, column, options),
      normalizeColumnOrder(columnOrder)
    );
}

export function hideProviderColumn(columnOrder = [], columnId = '') {
  if (!columnId) {
    return normalizeColumnOrder(columnOrder);
  }

  return normalizeColumnOrder(columnOrder).map((column) => {
    if (column.id !== columnId) return column;

    return {
      ...column,
      visible: false,
    };
  });
}

export function showProviderColumn(columnOrder = [], columnId = '') {
  if (!columnId) {
    return normalizeColumnOrder(columnOrder);
  }

  const normalized = normalizeColumnOrder(columnOrder);
  const nextOrder = getNextColumnOrder(normalized);

  return normalizeColumnOrder(
    normalized.map((column) => {
      if (column.id !== columnId) return column;

      return {
        ...column,
        visible: true,
        order: column.visible === false ? nextOrder : column.order,
      };
    })
  );
}

export function removeProviderColumn(columnOrder = [], columnId = '') {
  if (!columnId) {
    return normalizeColumnOrder(columnOrder);
  }

  return normalizeColumnOrder(columnOrder).filter(
    (column) => column.id !== columnId
  );
}

export function hideProviderColumnsForProvider(columnOrder = [], provider = '') {
  const providerId = normalizeProviderId(provider);

  if (!providerId) {
    return normalizeColumnOrder(columnOrder);
  }

  return normalizeColumnOrder(columnOrder).map((column) => {
    const spec = getProviderColumnSpec(column);

    if (!spec.isProviderColumn || spec.provider !== providerId) {
      return column;
    }

    return {
      ...column,
      visible: false,
    };
  });
}

export function removeProviderColumnsForProvider(columnOrder = [], provider = '') {
  const providerId = normalizeProviderId(provider);

  if (!providerId) {
    return normalizeColumnOrder(columnOrder);
  }

  return normalizeColumnOrder(columnOrder).filter((column) => {
    const spec = getProviderColumnSpec(column);

    return !spec.isProviderColumn || spec.provider !== providerId;
  });
}

export function getProviderColumnsForProvider(columnOrder = [], provider = '') {
  const providerId = normalizeProviderId(provider);

  if (!providerId) return [];

  return normalizeColumnOrder(columnOrder).filter((column) => {
    const spec = getProviderColumnSpec(column);

    return spec.isProviderColumn && spec.provider === providerId;
  });
}

export function pruneColumnWidthsForColumnOrder({
  columnWidths = {},
  columnOrder = [],
}) {
  const normalized = normalizeColumnOrder(columnOrder);
  const activeIds = new Set(normalized.map((column) => column.id));

  return Object.entries(columnWidths || {}).reduce((acc, [columnId, width]) => {
    if (activeIds.has(columnId)) {
      acc[columnId] = width;
    }

    return acc;
  }, {});
}